<template>
    <div
        :class="containerClass"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
    >
        <div
            class="notification-message-content"
            :class="message.contentStyleClass"
        >
            <span
                :class="iconClass"
                class="text-[24px] md:text-[32px]"
            />
            <div class="notification-message-text">
                <template v-if="!message.component">
                    <span class="notifications-summary">
                        {{ message.title }}
                    </span>
                    <div class="notifications-detail">
                        {{ message.message }}
                    </div>
                </template>
                <component
                    :is="message.component"
                    v-else
                    :message="message"
                    v-bind="message.componentAttrs"
                    @close="onCloseClick"
                />
            </div>
            <button
                v-if="isClosable"
                class="icon-close"
                type="button"
                @click="onCloseClick"
            >
                <span class="vc-icon-close" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { ToastMessageOptions } from '@/types/notify'

const DEFAULT_DURATION = 4500
const BASE_CLASS = 'notification-message'

const TYPE_CLASSES: {[key: string]: string} = {
    info: 'notification-message-info',
    warning: 'notification-message-warn',
    error: 'notification-message-error',
    success: 'notification-message-success'
}
const TYPE_ICONS: {[key: string]: string} = {
    info: 'vc-icon-info',
    warning: 'vc-icon-alert',
    error: 'vc-icon-cross',
    success: 'vc-icon-checkmark'
}

/* Props */
const props = defineProps<{
    message: ToastMessageOptions
}>()
/* Emits */
const emit = defineEmits<{(e: 'close', pl?: ToastMessageOptions): void}>()

/* Data */
const closeTimeout = ref<null | number>(null)

/* Computed */
const containerClass = computed(() => {
    const type = props.message?.type || ''
    const typeClass = TYPE_CLASSES[type] || ''
    return [
        BASE_CLASS,
        props.message?.styleClass || '',
        typeClass
    ]
})
const iconClass = computed(() => {
    const type = props.message?.type || ''
    const icon = TYPE_ICONS[type] || ''
    return [
        'notification-message-icon', icon
    ]
})
const isClosable = computed(() => {
    return props.message?.closable !== false || props.message?.duration === 0
})


/* Methods */
function onCloseClick (): void {
    clearCloseTimeout()
    close()
}
function close () {
    emit('close', props.message)
}
function clearCloseTimeout () {
    if (closeTimeout.value) {
        clearTimeout(closeTimeout.value)
        closeTimeout.value = null
    }
}

/* Mounted */
onMounted(() => {
    const duration = props.message?.duration === undefined ? DEFAULT_DURATION : props.message?.duration
    if (duration) {
        closeTimeout.value = window.setTimeout(() => {
            close()
        }, duration)
    }
})

/* Unmount */
onBeforeUnmount(() => {
    clearCloseTimeout()
})

/* Expose */
defineExpose({
    close: onCloseClick
})
</script>
